import {
  createSliderRow,
  type MountedExperiment,
  type SliderRowHandle,
} from '@learnforge/demo-kit';
import {
  evaluate,
  formalInverse,
  isOneToOneNumeric,
  parse,
  sampleCurve,
  suggestRestriction,
  type ExprNode,
  type Interval,
} from '../func';
import { stringify, traverse } from '@learnforge/demo-math/expr';
import { createActionBar } from '../action-bar';
import {
  createFunctionGraph,
  type FunctionGraphCurveClearance,
  type FunctionGraphHandle,
} from '../render/function-graph';
import {
  createCartesianViewport,
  createFormulaLegend,
  createSvgEl,
  type CartesianViewportView,
} from '@learnforge/demo-render';
import type { FuncContext } from '../types';

const AXIS_ASYMPTOTE_CLEARANCE_PX = 3;

function axisAsymptoteClearances(node: ExprNode): FunctionGraphCurveClearance[] {
  const formula = stringify(node);
  if (formula === 'exp(x)') {
    return [
      {
        kind: 'horizontal',
        value: 0,
        side: 'positive',
        pixels: AXIS_ASYMPTOTE_CLEARANCE_PX,
      },
    ];
  }
  if (formula === 'ln(x)' || formula === 'log(x)') {
    return [
      {
        kind: 'vertical',
        value: 0,
        side: 'positive',
        pixels: AXIS_ASYMPTOTE_CLEARANCE_PX,
      },
    ];
  }
  return [];
}

export interface SwappedPoint {
  x: number;
  y: number;
}

export function computeInverseSample(
  node: ExprNode,
  xMin: number,
  xMax: number,
  samples: number,
): SwappedPoint[] {
  const out: SwappedPoint[] = [];
  for (let i = 0; i < samples; i++) {
    const x = xMin + ((xMax - xMin) * i) / (samples - 1);
    const y = evaluate(node, x);
    if (Number.isFinite(y)) out.push({ x: y, y: x });
  }
  return out;
}

export function sampleInverseCurve(
  node: ExprNode,
  sourceDomain: [number, number],
  visibleXDomain: [number, number],
  samples: number,
): Float64Array {
  const buf = new Float64Array(samples);
  buf.fill(Number.NaN);

  const scanCount = Math.max(2049, samples * 4 + 1);
  const runs: Array<Array<{ source: number; value: number }>> = [];
  let run: Array<{ source: number; value: number }> = [];
  for (let index = 0; index < scanCount; index += 1) {
    const source =
      sourceDomain[0] + ((sourceDomain[1] - sourceDomain[0]) * index) / (scanCount - 1);
    const value = evaluate(node, source);
    if (Number.isFinite(value)) {
      run.push({ source, value });
    } else if (run.length > 0) {
      runs.push(run);
      run = [];
    }
  }
  if (run.length > 0) runs.push(run);

  for (let index = 0; index < samples; index += 1) {
    const target =
      visibleXDomain[0] +
      ((visibleXDomain[1] - visibleXDomain[0]) * index) / Math.max(1, samples - 1);
    const matchingRun = runs.find((candidate) => {
      const first = candidate[0];
      const last = candidate.at(-1);
      if (!first || !last) return false;
      return (
        target >= Math.min(first.value, last.value) && target <= Math.max(first.value, last.value)
      );
    });
    const first = matchingRun?.[0];
    const last = matchingRun?.at(-1);
    if (!first || !last) continue;

    let lower = first.source;
    let upper = last.source;
    const increasing = first.value <= last.value;
    for (let iteration = 0; iteration < 52; iteration += 1) {
      const midpoint = (lower + upper) / 2;
      const value = evaluate(node, midpoint);
      if (!Number.isFinite(value)) break;
      if (value < target === increasing) lower = midpoint;
      else upper = midpoint;
    }
    buf[index] = (lower + upper) / 2;
  }
  return buf;
}

function intervalContains(interval: Interval, value: number): boolean {
  const afterStart = interval.openLeft ? value > interval.start : value >= interval.start;
  const beforeEnd = interval.openRight ? value < interval.end : value <= interval.end;
  return afterStart && beforeEnd;
}

function formalInverseMatchesRestriction(
  functionNode: ExprNode,
  inverseNode: ExprNode,
  restriction: Interval,
): boolean {
  if (!Number.isFinite(restriction.start) || !Number.isFinite(restriction.end)) return true;
  for (const fraction of [0.25, 0.5, 0.75]) {
    const source = restriction.start + (restriction.end - restriction.start) * fraction;
    const output = evaluate(functionNode, source);
    const recoveredSource = evaluate(inverseNode, output);
    const tolerance = 1e-7 * Math.max(1, Math.abs(source));
    if (!Number.isFinite(recoveredSource) || Math.abs(recoveredSource - source) > tolerance) {
      return false;
    }
  }
  return true;
}

function isValidInverseInput(
  functionNode: ExprNode,
  inverseNode: ExprNode,
  sourceRestriction: Interval | null,
  input: number,
): boolean {
  const source = evaluate(inverseNode, input);
  if (!Number.isFinite(source)) return false;
  if (sourceRestriction && !intervalContains(sourceRestriction, source)) return false;
  const roundTrip = evaluate(functionNode, source);
  const tolerance = 1e-7 * Math.max(1, Math.abs(input));
  return Number.isFinite(roundTrip) && Math.abs(roundTrip - input) <= tolerance;
}

export function sampleFormalInverseCurve(
  functionNode: ExprNode,
  inverseNode: ExprNode,
  sourceRestriction: Interval | null,
  visibleXDomain: [number, number],
  samples: number,
): Float64Array {
  const buf = sampleCurve(inverseNode, visibleXDomain[0], visibleXDomain[1], samples);
  for (let index = 0; index < buf.length; index += 1) {
    const input =
      visibleXDomain[0] +
      ((visibleXDomain[1] - visibleXDomain[0]) * index) / Math.max(1, samples - 1);
    if (!isValidInverseInput(functionNode, inverseNode, sourceRestriction, input)) {
      buf[index] = Number.NaN;
    }
  }
  return buf;
}

export function sampleRestrictedFunctionCurve(
  functionNode: ExprNode,
  visibleXDomain: [number, number],
  samples: number,
  sourceRestriction: Interval,
): Float64Array {
  const buf = sampleCurve(functionNode, visibleXDomain[0], visibleXDomain[1], samples);
  for (let index = 0; index < buf.length; index += 1) {
    const x =
      visibleXDomain[0] +
      ((visibleXDomain[1] - visibleXDomain[0]) * index) / Math.max(1, samples - 1);
    if (!intervalContains(sourceRestriction, x)) buf[index] = Number.NaN;
  }
  return buf;
}

function formatRestrictionBound(value: number): string {
  if (!Number.isFinite(value)) return value < 0 ? '−∞' : '∞';
  if (Math.abs(value) < 1e-12) return '0';
  const sign = value < 0 ? '−' : '';
  const magnitude = Math.abs(value);
  if (Math.abs(magnitude - Math.PI) < 1e-12) return `${sign}π`;
  if (Math.abs(magnitude - Math.PI / 2) < 1e-12) return `${sign}π/2`;
  return value.toFixed(3).replace(/(?:\.0+|(\.\d*?)0+)$/, '$1');
}

function restrictionFormula(interval: Interval, variableName: string): string {
  if (!Number.isFinite(interval.end)) {
    return `${variableName} ${interval.openLeft ? '>' : '≥'} ${formatRestrictionBound(interval.start)}`;
  }
  if (!Number.isFinite(interval.start)) {
    return `${variableName} ${interval.openRight ? '<' : '≤'} ${formatRestrictionBound(interval.end)}`;
  }
  return `${formatRestrictionBound(interval.start)} ${interval.openLeft ? '<' : '≤'} ${variableName} ${interval.openRight ? '<' : '≤'} ${formatRestrictionBound(interval.end)}`;
}

function snapDomainBoundary(value: number): number {
  const roundedInteger = Math.round(value);
  if (Math.abs(value - roundedInteger) < 1e-4) return roundedInteger;
  return Number(value.toFixed(6));
}

function inverseDomainIntervals(
  functionNode: ExprNode,
  inverseNode: ExprNode,
  sourceRestriction: Interval | null,
): Interval[] {
  // Keep the dense scan numerically stable, then probe around constants from both
  // formulas so translated ranges (for example x >= 200) are not missed.
  const xs = Array.from({ length: 4001 }, (_, index) => -100 + (200 * index) / 4000);
  const anchors = new Set<number>([0]);
  for (const node of [functionNode, inverseNode]) {
    traverse(node, (candidate) => {
      if (candidate.kind === 'num' && Number.isFinite(candidate.value)) {
        anchors.add(candidate.value);
        anchors.add(-candidate.value);
      }
    });
  }
  for (const anchor of anchors) {
    for (const offset of [-100, -10, -1, -0.5, 0, 0.5, 1, 10, 100]) {
      xs.push(anchor + offset);
    }
  }
  xs.sort((left, right) => left - right);
  const distinctXs = xs.filter((value, index) => index === 0 || value !== xs[index - 1]);
  const valid = distinctXs.map((input) =>
    isValidInverseInput(functionNode, inverseNode, sourceRestriction, input),
  );

  function transitionBoundary(left: number, right: number, leftIsValid: boolean): number {
    let lower = left;
    let upper = right;
    for (let iteration = 0; iteration < 52; iteration += 1) {
      const midpoint = (lower + upper) / 2;
      if (
        isValidInverseInput(functionNode, inverseNode, sourceRestriction, midpoint) === leftIsValid
      ) {
        lower = midpoint;
      } else {
        upper = midpoint;
      }
    }
    return snapDomainBoundary((lower + upper) / 2);
  }

  const intervals: Interval[] = [];
  let start: number | null = valid[0] ? -Infinity : null;
  let openLeft = true;
  for (let index = 1; index < distinctXs.length; index += 1) {
    if (!valid[index - 1] && valid[index]) {
      start = transitionBoundary(distinctXs[index - 1]!, distinctXs[index]!, false);
      openLeft = !isValidInverseInput(functionNode, inverseNode, sourceRestriction, start);
    } else if (valid[index - 1] && !valid[index] && start !== null) {
      const end = transitionBoundary(distinctXs[index - 1]!, distinctXs[index]!, true);
      intervals.push({
        start,
        end,
        openLeft,
        openRight: !isValidInverseInput(functionNode, inverseNode, sourceRestriction, end),
      });
      start = null;
    }
  }
  if (start !== null && valid.at(-1)) {
    intervals.push({ start, end: Infinity, openLeft, openRight: true });
  }
  return intervals;
}

function inverseDomainFormula(
  functionNode: ExprNode,
  inverseNode: ExprNode,
  sourceRestriction: Interval | null,
): string | null {
  const intervals = inverseDomainIntervals(functionNode, inverseNode, sourceRestriction);
  if (intervals.length === 0) return null;
  if (
    intervals.length === 1 &&
    intervals[0]?.start === -Infinity &&
    intervals[0]?.end === Infinity
  ) {
    return '';
  }
  if (
    intervals.length === 2 &&
    intervals[0]?.start === -Infinity &&
    intervals[1]?.end === Infinity &&
    intervals[0].openRight &&
    intervals[1].openLeft &&
    Math.abs(intervals[0].end - intervals[1].start) < 1e-6
  ) {
    return `x ≠ ${formatRestrictionBound(intervals[0].end)}`;
  }
  if (intervals.length === 1) return restrictionFormula(intervals[0]!, 'x');
  return intervals
    .map(
      (interval) =>
        `${interval.openLeft ? '(' : '['}${formatRestrictionBound(interval.start)}, ${formatRestrictionBound(interval.end)}${interval.openRight ? ')' : ']'}`,
    )
    .join(' ∪ ');
}

export function mountInverses(ctx: FuncContext): MountedExperiment {
  let fSrc = 'exp(x)';
  let restrictionOn = false;
  let manualDomain: [number, number] = [-10, 10];
  let showYx = true;

  const svg = createSvgEl('svg', {
    viewBox: '0 0 600 600',
    class: 'fn-stage fn-stage-inverse',
    preserveAspectRatio: 'xMidYMid meet',
  });
  ctx.root.appendChild(svg);
  const stageG = createSvgEl('g');
  svg.appendChild(stageG);
  const legend = createFormulaLegend(ctx.root);
  let view: CartesianViewportView = { xDomain: [-10, 10], yDomain: [-10, 10], zoom: 1 };

  const messageEl = document.createElement('div');
  messageEl.className = 'fn-message';
  ctx.root.appendChild(messageEl);

  let restrictionCheckbox: HTMLInputElement | null = null;
  let restrictionText: HTMLSpanElement | null = null;
  const customDomainControls = document.createElement('div');
  customDomainControls.className = 'fn-custom-domain-controls';
  customDomainControls.dataset.customDomainControls = '';
  customDomainControls.hidden = true;
  const manualDomainHandles: Partial<Record<'start' | 'end', SliderRowHandle>> = {};

  function syncManualDomainControls(): void {
    manualDomainHandles.start?.setValue(manualDomain[0]);
    manualDomainHandles.end?.setValue(manualDomain[1]);
  }

  let graphHandle: FunctionGraphHandle | null = null;
  function redraw(): void {
    const fNode = parse(fSrc).node;
    const isOneToOne = isOneToOneNumeric(fNode, -10, 10);
    const suggestedRestriction = !isOneToOne ? suggestRestriction(fNode) : null;
    const customRestrictionMode = !isOneToOne && suggestedRestriction === null;
    const manualRestriction: Interval = {
      start: manualDomain[0],
      end: manualDomain[1],
      openLeft: false,
      openRight: false,
    };
    const manualRestrictionValid =
      customRestrictionMode &&
      restrictionOn &&
      manualDomain[0] < manualDomain[1] &&
      isOneToOneNumeric(fNode, manualDomain[0], manualDomain[1]);
    const availableRestriction =
      suggestedRestriction ?? (manualRestrictionValid ? manualRestriction : null);
    const activeRestriction = restrictionOn ? availableRestriction : null;
    let xMin = -10;
    let xMax = 10;
    if (activeRestriction) {
      xMin = Math.max(-10, activeRestriction.start);
      xMax = Math.min(10, activeRestriction.end);
    }

    const sampleCount = Math.max(320, Math.round(600 * (globalThis.devicePixelRatio || 1)));
    const fSample = activeRestriction
      ? sampleRestrictedFunctionCurve(fNode, view.xDomain, sampleCount, activeRestriction)
      : sampleCurve(fNode, view.xDomain[0], view.xDomain[1], sampleCount);
    const inverseVisible = isOneToOne || activeRestriction !== null;
    const candidateInverseNode = inverseVisible ? formalInverse(fNode) : null;
    const inverseNode =
      candidateInverseNode &&
      (!activeRestriction ||
        !customRestrictionMode ||
        formalInverseMatchesRestriction(fNode, candidateInverseNode, activeRestriction))
        ? candidateInverseNode
        : null;
    const inverseBuf = inverseNode
      ? sampleFormalInverseCurve(fNode, inverseNode, activeRestriction, view.xDomain, sampleCount)
      : sampleInverseCurve(fNode, [xMin, xMax], view.xDomain, sampleCount);
    const branchFormula = activeRestriction ? restrictionFormula(activeRestriction, 'x') : '';
    const inverseDomain = inverseNode
      ? inverseDomainFormula(fNode, inverseNode, activeRestriction)
      : null;

    if (restrictionCheckbox && restrictionText) {
      restrictionCheckbox.disabled = isOneToOne;
      restrictionText.textContent = isOneToOne
        ? ctx.i18n.t('param.restrict.not-needed')
        : suggestedRestriction
          ? `${ctx.i18n.t('param.restrict.use-branch')} ${restrictionFormula(suggestedRestriction, 'x')}`
          : ctx.i18n.t('param.restrict.custom');
    }
    customDomainControls.hidden = !customRestrictionMode || !restrictionOn;

    const options = {
      xDomain: view.xDomain,
      yDomain: view.yDomain,
      width: 600,
      height: 600,
      curves: [
        {
          sample: fSample,
          className: 'fn-curve-primary',
          label: 'f(x)',
          asymptoteClearances: axisAsymptoteClearances(fNode),
        },
        ...(inverseVisible
          ? [
              {
                sample: inverseBuf,
                className: 'fn-curve-secondary',
                label: 'f⁻¹(x)',
                asymptoteClearances: inverseNode ? axisAsymptoteClearances(inverseNode) : [],
              },
            ]
          : []),
      ],
      showYxLine: showYx,
      i18n: ctx.i18n,
    };
    if (graphHandle) graphHandle.update(options);
    else graphHandle = createFunctionGraph(stageG, options);
    legend.setEntries([
      {
        id: 'function',
        formula: `f(x) = ${fSrc}${branchFormula ? `, ${branchFormula}` : ''}`,
        color: 'var(--accent)',
      },
      ...(inverseVisible
        ? [
            {
              id: 'inverse',
              formula: inverseNode
                ? `f⁻¹(x) = ${stringify(inverseNode)}${inverseDomain === null ? `, ${ctx.i18n.t('message.inverse-domain-unresolved')}` : inverseDomain ? `, ${inverseDomain}` : ''}`
                : `f⁻¹(x) = ${ctx.i18n.t('message.numerical-inverse')}`,
              color: 'var(--red)',
              lineStyle: 'dashed' as const,
            },
          ]
        : []),
      ...(showYx
        ? [{ id: 'yx', formula: 'y = x', color: 'var(--ink)', lineStyle: 'dashed' as const }]
        : []),
    ]);
    messageEl.textContent =
      !isOneToOne && !activeRestriction
        ? customRestrictionMode && restrictionOn
          ? ctx.i18n.t('message.restriction-not-one-to-one')
          : ctx.i18n.t('message.not-invertible-without-restriction')
        : '';
  }

  const fRow = document.createElement('div');
  fRow.className = 'fn-panel-row';
  const fLabel = document.createElement('label');
  fLabel.textContent = ctx.i18n.t('param.f.label');
  const fInput = document.createElement('input');
  fInput.type = 'text';
  fInput.value = fSrc;
  fInput.addEventListener('change', () => {
    try {
      parse(fInput.value);
      fSrc = fInput.value;
      restrictionOn = false;
      manualDomain = [-10, 10];
      syncManualDomainControls();
      if (restrictionCheckbox) restrictionCheckbox.checked = false;
      fInput.classList.remove('fn-input-error');
      redraw();
    } catch {
      fInput.classList.add('fn-input-error');
    }
  });
  fRow.appendChild(fLabel);
  fRow.appendChild(fInput);
  ctx.panel.appendChild(fRow);

  function addToggle(
    label: string,
    initial: boolean,
    onChange: (value: boolean) => void,
  ): { checkbox: HTMLInputElement; text: HTMLSpanElement } {
    const row = document.createElement('div');
    row.className = 'toggle-row';
    const l = document.createElement('label');
    l.className = 'toggle-lbl';
    const cb = document.createElement('input');
    cb.type = 'checkbox';
    cb.checked = initial;
    cb.addEventListener('change', () => onChange(cb.checked));
    const text = document.createElement('span');
    text.className = 'toggle-text';
    text.textContent = label;
    l.append(cb, text);
    row.appendChild(l);
    ctx.panel.appendChild(row);
    return { checkbox: cb, text };
  }

  const restrictionToggle = addToggle(
    ctx.i18n.t('param.restrict.label'),
    restrictionOn,
    (value) => {
      restrictionOn = value;
      redraw();
    },
  );
  restrictionCheckbox = restrictionToggle.checkbox;
  restrictionCheckbox.dataset.restrictionToggle = '';
  restrictionText = restrictionToggle.text;

  for (const definition of [
    { key: 'start' as const, index: 0 as const, labelKey: 'param.domain-start.label' as const },
    { key: 'end' as const, index: 1 as const, labelKey: 'param.domain-end.label' as const },
  ]) {
    const handle = createSliderRow({
      id: `inverse-domain-${definition.key}`,
      labelKey: definition.labelKey,
      i18n: ctx.i18n,
      min: -10,
      max: 10,
      step: 0.1,
      value: manualDomain[definition.index],
      format: (value) => value.toFixed(2),
      onChange: (value) => {
        const next: [number, number] = [...manualDomain];
        next[definition.index] = value;
        if (next[0] > next[1]) next.sort((left, right) => left - right);
        manualDomain = next;
        syncManualDomainControls();
        redraw();
      },
    });
    manualDomainHandles[definition.key] = handle;
    customDomainControls.appendChild(handle.element);
  }
  ctx.panel.appendChild(customDomainControls);

  addToggle(ctx.i18n.t('param.show-yx.label'), showYx, (value) => {
    showYx = value;
    redraw();
  });

  redraw();
  const viewport = createCartesianViewport(svg, {
    base: { xDomain: [-10, 10], yDomain: [-10, 10] },
    plotRect: { x: 0, y: 0, width: 600, height: 600 },
    onChange(next) {
      view = next;
      redraw();
    },
  });

  createActionBar(
    ctx.actionBar,
    [
      {
        id: 'reset',
        labelKey: ctx.i18n.t('btn.reset'),
        onClick: () => {
          fSrc = 'exp(x)';
          restrictionOn = false;
          manualDomain = [-10, 10];
          fInput.value = fSrc;
          syncManualDomainControls();
          if (restrictionCheckbox) restrictionCheckbox.checked = false;
          redraw();
        },
      },
    ],
    { viewReset: { labelKey: ctx.i18n.t('btn.reset-view'), onResetView: () => viewport.reset() } },
  );
  return {
    dispose() {
      graphHandle?.destroy();
      viewport.dispose();
      legend.destroy();
      svg.remove();
      messageEl.remove();
      ctx.panel.replaceChildren();
      ctx.actionBar.replaceChildren();
    },
  };
}
